import React, { useState, useEffect } from 'react';
import './CarritoFlotante.css';
import { formatPrice } from '../utils/format';
import { getRole, getUserData } from '../services/auth';
import { fetchMiPerfil, fetchMisDirecciones } from '../services/api';

const camposContacto = [
  { name: "nombreCliente", label: "Nombre completo",       type: "text" },
  { name: "rutCliente",    label: "RUT (ej: 12345678-9)", type: "text", placeholder: "12345678-9" },
  { name: "email",         label: "Email de contacto",    type: "email" },
  { name: "telefono",      label: "Teléfono",             type: "tel" },
];

const camposDireccion = [
  { name: "direccion", label: "Dirección", type: "text" },
  { name: "comuna",    label: "Comuna",    type: "text" },
  { name: "ciudad",    label: "Ciudad",    type: "text" },
];

const inicial = {
  nombreCliente: "", rutCliente: "", email: "", telefono: "",
  direccion: "", comuna: "", ciudad: "",
};

function CarritoFlotante({ detalleVenta, total, onPagar, onActualizarItem, onEliminarItem }) {
  const [visible,    setVisible]    = useState(false);
  const [paso,       setPaso]       = useState("carrito"); // carrito | contacto | direccion
  const [datos,      setDatos]      = useState(inicial);
  const [enviando,   setEnviando]   = useState(false);
  const [direcciones, setDirecciones] = useState([]);
  const [dirSelId,   setDirSelId]   = useState(null);

  const estaLogueado = !!localStorage.getItem("token");
  const role = getRole();

  // Pre-cargar perfil y direcciones guardadas si está logueado
  useEffect(() => {
    if (!estaLogueado) return;

    // Para CLIENT: usar datos de sesión guardados en localStorage (fuente primaria)
    if (role === "CLIENT") {
      const ud = getUserData();
      if (ud) {
        setDatos(prev => ({
          ...prev,
          nombreCliente: ud.nombre ? `${ud.nombre} ${ud.apellido || ""}`.trim() : prev.nombreCliente,
          rutCliente:    ud.rut      || prev.rutCliente,
          email:         ud.email    || prev.email,
          telefono:      ud.telefono || prev.telefono,
          direccion:     ud.direccion || prev.direccion,
          comuna:        ud.comuna    || prev.comuna,
          ciudad:        ud.ciudad    || prev.ciudad,
        }));
      } else {
        // Fallback: intentar desde el API si no hay datos en localStorage
        fetchMiPerfil()
          .then(res => {
            const u = res.data || {};
            setDatos(prev => ({
              ...prev,
              nombreCliente: u.nombre ? `${u.nombre} ${u.apellido || ""}`.trim() : prev.nombreCliente,
              rutCliente:    u.rut      || prev.rutCliente,
              email:         u.email    || prev.email,
              telefono:      u.telefono || prev.telefono,
            }));
          })
          .catch(() => {});
      }
    }

    fetchMisDirecciones()
      .then(res => setDirecciones(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [estaLogueado, role]);

  const handleCampo = (e) =>
    setDatos(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSelDireccion = (dir) => {
    setDirSelId(dir.id);
    setDatos(prev => ({
      ...prev,
      direccion: dir.direccion,
      comuna:    dir.comuna,
      ciudad:    dir.ciudad,
    }));
  };

  const handlePagar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await onPagar(datos);
      setDatos(inicial);
      setPaso("carrito");
      setDirSelId(null);
    } finally {
      setEnviando(false);
    }
  };

  const cerrar = () => { setVisible(false); setPaso("carrito"); };
  const cantItems = detalleVenta.length;

  return (
    <div className="carrito-flotante">
      <button className="toggle-carrito" onClick={() => setVisible(!visible)}>
        🛒 ({cantItems})
      </button>

      {visible && (
        <div className="carrito-detalle">

          {/* ── PASO 1: CARRITO ── */}
          {paso === "carrito" && (
            <>
              <Header titulo="Carrito de Compras" onCerrar={cerrar} />
              {cantItems === 0 ? (
                <p style={s.vacio}>Tu carrito está vacío</p>
              ) : (
                <>
                  <ul style={{ listStyle: "none", padding: 0, margin: "12px 0" }}>
                    {detalleVenta.map((item, i) => (
                      <li key={i} style={s.item}>
                        <strong>{item.nombre}</strong>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                          <span>Cant:</span>
                          <input
                            type="number" min="1" value={item.cantidad}
                            onChange={e => {
                              const n = parseFloat(e.target.value);
                              if (!isNaN(n) && n > 0) onActualizarItem(i, n);
                            }}
                            style={s.inputCant}
                          />
                          <span style={{ marginLeft: "auto" }}>${formatPrice(item.subtotal)} <small style={{ color: '#888', fontWeight: 'normal' }}>+ IVA</small></span>
                          <button onClick={() => onEliminarItem(i)} style={s.btnEliminar}>✕</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Total total={total} />
                  <button style={s.btnPrimario} onClick={() => setPaso("contacto")}>
                    Continuar →
                  </button>
                </>
              )}
            </>
          )}

          {/* ── PASO 2: DATOS DE CONTACTO ── */}
          {paso === "contacto" && (
            <>
              <Header titulo="Datos de contacto" onVolver={() => setPaso("carrito")} onCerrar={cerrar} />
              <form onSubmit={e => { e.preventDefault(); setPaso("direccion"); }}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {camposContacto.map(({ name, label, type, placeholder }) => (
                  <Campo key={name} name={name} label={label} type={type}
                    placeholder={placeholder} value={datos[name]} onChange={handleCampo} />
                ))}
                <button type="submit" style={s.btnPrimario}>Continuar →</button>
              </form>
            </>
          )}

          {/* ── PASO 3: DIRECCIÓN DE ENVÍO ── */}
          {paso === "direccion" && (
            <>
              <Header titulo="Dirección de envío" onVolver={() => setPaso("contacto")} onCerrar={cerrar} />

              {/* Direcciones guardadas */}
              {direcciones.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <p style={s.labelSeccion}>Mis direcciones guardadas:</p>
                  {direcciones.map(dir => (
                    <button key={dir.id} onClick={() => handleSelDireccion(dir)}
                      style={{ ...s.btnDireccion, ...(dirSelId === dir.id ? s.btnDireccionActivo : {}) }}>
                      📍 {dir.direccion}, {dir.comuna}, {dir.ciudad}
                    </button>
                  ))}
                  <hr style={{ margin: "10px 0", borderColor: "#eee" }} />
                  <p style={s.labelSeccion}>O ingresa una nueva:</p>
                </div>
              )}

              <form onSubmit={handlePagar}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {camposDireccion.map(({ name, label, type }) => (
                  <Campo key={name} name={name} label={label} type={type}
                    value={datos[name]} onChange={handleCampo} />
                ))}
                <Total total={total} />
                <button type="submit" style={s.btnPrimario} disabled={enviando}>
                  {enviando ? "Procesando..." : "💳 Pagar con MercadoPago"}
                </button>
              </form>
            </>
          )}

        </div>
      )}
    </div>
  );
}

// ── Subcomponentes ──────────────────────────────────────────
function Header({ titulo, onVolver, onCerrar }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {onVolver && (
          <button onClick={onVolver} style={s.btnVolver}>← Volver</button>
        )}
        <h4 style={{ margin: 0 }}>{titulo}</h4>
      </div>
      <button onClick={onCerrar} style={s.btnCerrar}>✕</button>
    </div>
  );
}

function Campo({ name, label, type, placeholder, value, onChange }) {
  return (
    <div>
      <label style={s.label}>{label}</label>
      <input name={name} type={type} value={value} onChange={onChange}
        placeholder={placeholder || ""} required style={s.input} />
    </div>
  );
}

function Total({ total }) {
  const neto         = parseFloat(total) || 0;
  const iva          = Math.round(neto * 0.19);
  const totalConIVA  = Math.round(neto + iva);
  return (
    <div style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#555", marginBottom: 3 }}>
        <span>Subtotal (neto)</span>
        <span>${formatPrice(neto)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#555", marginBottom: 6 }}>
        <span>IVA (19%)</span>
        <span>${formatPrice(iva)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: 15 }}>
        <span>Total a pagar</span>
        <span>${formatPrice(totalConIVA)}</span>
      </div>
    </div>
  );
}

// ── Estilos ─────────────────────────────────────────────────
const s = {
  btnCerrar:  { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#666" },
  btnEliminar:{ background: "none", border: "none", color: "red", cursor: "pointer", fontSize: 12 },
  btnVolver:  { background: "none", border: "none", cursor: "pointer", color: "#444", fontSize: 13, padding: 0 },
  btnPrimario:{
    padding: "10px 0", backgroundColor: "#1a1a2e", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 15, cursor: "pointer", width: "100%", marginTop: 4,
  },
  btnDireccion:{
    display: "block", width: "100%", textAlign: "left", padding: "6px 10px",
    marginBottom: 6, border: "1px solid #ddd", borderRadius: 6, cursor: "pointer",
    background: "#f9f9f9", fontSize: 12, color: "#000",
  },
  btnDireccionActivo:{
    background: "#e0f2fe", borderColor: "#0284c7", fontWeight: 600,
  },
  item:     { borderBottom: "1px solid #eee", paddingBottom: 8, marginBottom: 8 },
  inputCant:{ width: 48, padding: "2px 4px", border: "1px solid #ccc", borderRadius: 4 },
  label:    { display: "block", fontSize: 12, marginBottom: 2, color: "#555" },
  input:    { width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, boxSizing: "border-box" },
  vacio:    { textAlign: "center", color: "#888", marginTop: 16 },
  labelSeccion: { fontSize: 12, color: "#666", margin: "0 0 6px", fontWeight: 600 },
};

export default CarritoFlotante;
